import React, { useState, useEffect } from "react";
import ModalWrapper from "../../components/ui/modals/ModalWrapper";
import Button from "../../components/ui/buttons/Button";
import { changePassword, dismissPasswordPrompt } from "../../services/api/core";
import { UI_COPY } from "../../config";
import { useUiText } from "../../hooks/useUiText";
import { mapServerPasswordErrors } from "../../utils/auth";

/**
 * Onboarding modal that prompts the user to change their password when the
 * account still uses a default or flagged credential. Shown from Home when
 * `personal_password` is false on the current user.
 *
 * @param {boolean} isOpen Modal visibility.
 * @param {Function} onClose Close without saving; modal may reappear on next visit.
 * @param {Function} onDismiss Mark prompt dismissed without changing password.
 * @param {Function} onComplete Called after a successful password change.
 * @param {string} username Username for password-manager accessibility.
 * @returns {JSX.Element|null}
 */
const PersonalPasswordModal = ({ isOpen, onClose, onDismiss, onComplete, username = '' }) => {
  const { language, t } = useUiText();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Client-side validation for current / new / confirm password fields.
   * Writes field errors into state.
   *
   * @returns {boolean} True when all fields pass.
   */
  const validateForm = () => {
    const newErrors = {};

    if (!oldPassword) {
      newErrors.oldPassword = t(UI_COPY.password.currentRequired);
    }

    if (!newPassword) {
      newErrors.newPassword = t(UI_COPY.password.newRequired);
    } else if (newPassword.length < 8) {
      newErrors.newPassword = t(UI_COPY.password.minLengthError);
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t(UI_COPY.password.confirmRequired);
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t(UI_COPY.password.mismatch);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submit the password change. Maps Django serializer errors onto the
   * local field-error shape, then calls `onComplete` on success.
   *
   * @returns {Promise<void>}
   */
  const handleUpdatePassword = async () => {
    // Validate before submit
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await changePassword(oldPassword, newPassword, confirmPassword);

      if (response.success) {
        onComplete();
      } else if (response.errors) {
        setErrors(mapServerPasswordErrors(response.errors, language));
      } else {
        setErrors({ general: t(UI_COPY.password.genericError) });
      }
    } catch (error) {
      console.error("Error updating password:", error);

      const responseData = error.response?.data;
      if (responseData?.errors) {
        setErrors(mapServerPasswordErrors(responseData.errors, language));
      } else {
        setErrors({ general: t(UI_COPY.password.genericError) });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Persist "dismiss password prompt" and notify the parent via `onDismiss`.
   *
   * @returns {Promise<void>}
   */
  const handleDismiss = async () => {
    setIsSubmitting(true);

    try {
      await dismissPasswordPrompt();
      onDismiss();
    } catch (error) {
      console.error("Error dismissing password prompt:", error);
      setErrors({ general: t(UI_COPY.password.genericError) });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
    }
  }, [isOpen]);

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col text-center">
        <h2 className="text-2xl font-bold mb-4">{t(UI_COPY.password.title)}</h2>
        <p className="text-sm text-gray-600 mb-6">
          {t(UI_COPY.password.subtitle)}
        </p>

        <form onSubmit={(e) => { e.preventDefault(); handleUpdatePassword(); }}>
        {/* Hidden username field for password manager accessibility */}
        <input
          type="text"
          id="username"
          name="username"
          autoComplete="username"
          value={username}
          readOnly
          style={{ position: 'absolute', left: '-9999px' }}
          tabIndex={-1}
          aria-hidden="true"
        />

        {/* Current Password Input */}
        <div className="mb-4">
          <label htmlFor="current-password" className="block text-left text-sm font-medium text-gray-700 mb-1">
            {t(UI_COPY.password.currentPassword)}
          </label>
          <input
            type="password"
            id="current-password"
            name="current-password"
            autoComplete="current-password"
            className={`w-full p-2 border rounded-md ${
              errors.oldPassword ? "border-red-500" : "border-gray-300"
            }`}
            placeholder={t(UI_COPY.password.currentPasswordPlaceholder)}
            value={oldPassword}
            onChange={(e) => {
              setOldPassword(e.target.value);
              setErrors({ ...errors, oldPassword: "", general: "" });
            }}
            disabled={isSubmitting}
          />
          {errors.oldPassword && (
            <p className="mt-1 text-sm text-red-600 text-left">{errors.oldPassword}</p>
          )}
        </div>

        {/* New Password Input */}
        <div className="mb-4">
          <label htmlFor="new-password" className="block text-left text-sm font-medium text-gray-700 mb-1">
            {t(UI_COPY.password.newPassword)}
          </label>
          <input
            type="password"
            id="new-password"
            name="new-password"
            autoComplete="new-password"
            className={`w-full p-2 border rounded-md ${
              errors.newPassword ? "border-red-500" : "border-gray-300"
            }`}
            placeholder={t(UI_COPY.password.newPasswordPlaceholder)}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setErrors({ ...errors, newPassword: "", general: "" });
            }}
            disabled={isSubmitting}
          />
          {errors.newPassword && (
            <p className="mt-1 text-sm text-red-600 text-left">{errors.newPassword}</p>
          )}
          <p className="mt-1 text-xs text-gray-600 text-left">
            <i>{t(UI_COPY.password.minLengthHint)}</i>
          </p>
        </div>

        {/* Confirm Password Input */}
        <div className="mb-6">
          <label htmlFor="confirm-password" className="block text-left text-sm font-medium text-gray-700 mb-1">
            {t(UI_COPY.password.confirmPassword)}
          </label>
          <input
            type="password"
            id="confirm-password"
            name="confirm-password"
            autoComplete="new-password"
            className={`w-full p-2 border rounded-md ${
              errors.confirmPassword ? "border-red-500" : "border-gray-300"
            }`}
            placeholder={t(UI_COPY.password.confirmPasswordPlaceholder)}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrors({ ...errors, confirmPassword: "", general: "" });
            }}
            disabled={isSubmitting}
          />
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600 text-left">{errors.confirmPassword}</p>
          )}
        </div>

        {/* General error message */}
        {errors.general && (
          <p className="mb-4 text-sm text-red-600">{errors.general}</p>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleDismiss}
            text={isSubmitting ? t(UI_COPY.password.processing) : t(UI_COPY.password.noThanks)}
            variant={isSubmitting ? "disabled" : "secondary"}
            className="w-full"
          />
          <Button
            onClick={handleUpdatePassword}
            text={isSubmitting ? t(UI_COPY.password.processing) : t(UI_COPY.password.updatePassword)}
            variant={isSubmitting ? "disabled" : "primary"}
            className="w-full"
          />
        </div>
        </form>
      </div>
    </ModalWrapper>
  );
};

export default PersonalPasswordModal;
